import React, { useMemo, useState } from 'react';
import { SectionProps } from '../../types';
import useOrderMethodAvailability from '../../../../../hooks/availability/useOrderMethodAvailability';
import useOrderPreparationTime from '../../../../../hooks/order/useOrderPreparationTime';
import { DeliveryMode, OrderMethod } from '../../../../../types/constants';
import { DeliveryProviderEnum, SidebarSectionEnum } from '../../enums';
import DeliveryRadiusModal from './DeliveryRadiusModal';
import { Button } from 'antd';
import {
  CheckSquareFilled,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import classnames from 'classnames';
import collect from 'collect.js';

const LABEL_BY_DELIVERY_PROVIDER: Record<DeliveryProviderEnum, string> = {
  [DeliveryProviderEnum.UberDrive]: 'Uber',
  [DeliveryProviderEnum.WoltDrive]: 'Wolt',
  [DeliveryProviderEnum.FirstDelivery]: 'Uber',
} as const;

const OrderMethodInfo = ({ data, ...rest }: SectionProps) => {
  const { isOrderMethodCurrentlyAvailable, deliveryEnabled } = useOrderMethodAvailability(data);
  const { getOrderMethodPrepTimeLabel } = useOrderPreparationTime(data);
  const getAvailabilityText = (orderMethod: OrderMethod): string => {
    if (!isOrderMethodCurrentlyAvailable(orderMethod)) {
      return 'No';
    }

    return `Yes ${getOrderMethodPrepTimeLabel(orderMethod)}`;
  };
  const isDeliveryAvailable = isOrderMethodCurrentlyAvailable(OrderMethod.Delivery);
  const orderMethods = useMemo(
    () => [
      {
        orderMethod: OrderMethod.Delivery,
        label: 'Delivery available',
        text: getAvailabilityText(OrderMethod.Delivery),
        isAvailable: isOrderMethodCurrentlyAvailable(OrderMethod.Delivery),
      },
      {
        orderMethod: OrderMethod.Pickup,
        label: 'Pickup available',
        text: getAvailabilityText(OrderMethod.Pickup),
        isAvailable: isOrderMethodCurrentlyAvailable(OrderMethod.Pickup),
      },
      {
        orderMethod: OrderMethod.DineIn,
        label: 'Dine-In available',
        text: data.has_dine_in ? 'Yes' : 'No',
        isAvailable: data.has_dine_in,
      },
      {
        orderMethod: 'Terrace',
        label: 'Terrace available',
        text: data.has_terrace ? 'Yes' : 'No',
        isAvailable: data.has_terrace,
      },
    ],
    [isOrderMethodCurrentlyAvailable, data.has_dine_in, data.has_terrace],
  );
  const deliveryProviders =
    isDeliveryAvailable &&
    collect(data.order_settings.delivery_providers)
      .map((provider) => LABEL_BY_DELIVERY_PROVIDER[provider])
      .unique()
      .implode(', ');
  const [isDeliveryZonesOpen, setIsDeliveryZonesOpen] = useState(false);
  const toggleDeliveryZonesOpen = () => setIsDeliveryZonesOpen((prev) => !prev);

  return (
    <>
      <section {...rest} id={SidebarSectionEnum.OrderMethodInfo}>
        <h4 className="text-md font-semibold text-slate-700 items-center">
          <TruckOutlined className="mr-2 size-4" />
          Delivery and Pickup
        </h4>
        {orderMethods.map(({ orderMethod, label, text, isAvailable }) => (
          <div key={orderMethod} className="flex justify-between text-slate-600 items-center">
            <p className="text-sm">{label}</p>
            <p
              className={classnames('text-sm font-medium', {
                'px-[10px] py-[2px] bg-green-500/10 rounded-lg text-green-500 hover:bg-slate-700/80':
                  isAvailable,
              })}
            >
              {isAvailable && <CheckSquareFilled className="mr-1" />}
              {text}
            </p>
          </div>
        ))}
        {deliveryEnabled && (
          <>
            <div className="flex justify-between text-slate-600 items-center">
              <p className="text-sm">Delivery partners</p>
              <p className="text-sm font-medium">
                {data.order_settings.delivery_mode === DeliveryMode.Internal
                  ? 'Restaurant has own drivers'
                  : deliveryProviders}
              </p>
            </div>
            <div className="flex justify-between text-slate-600 items-center">
              <p className="text-sm">Delivery radius</p>
              <Button
                className="text-xs font-medium bg-slate-700/5 rounded-full"
                onClick={toggleDeliveryZonesOpen}
              >
                <EnvironmentOutlined className="mr-1 text-xs" />
                View details
              </Button>
            </div>
            <p className="p-3 font-medium text-amber-400 text-xs border-sm rounded-lg bg-amber-400/10 border-amber-400/40 flex">
              <ExclamationCircleOutlined className="text-md text-amber-400 mr-2" />
              Upon delivery: Card payment not possible with the driver
            </p>
          </>
        )}
      </section>
      <DeliveryRadiusModal
        data={data}
        open={isDeliveryZonesOpen}
        onCancel={toggleDeliveryZonesOpen}
      />
    </>
  );
};

export default OrderMethodInfo;
